import React, { useEffect, useState } from 'react';
import ListDropDown from 'components/list-dropdown/ListDropDown';
import { CustomInput } from 'components';
import { CommissionType } from '../commission-settings/constants';
import { InputType } from 'components/custom-input.tsx/CustomInput';
import { cleanNumberInput, getCommissionBases, isValidDecimalInput } from '../utils';
import { OutlinedInfoIcon } from 'assets/icons';
import { CommissionBaseItem } from 'containers/app/app.model';
import { selectCommissionBaseMap } from 'containers/app/appSlice';
import { useAppSelector } from 'hooks';
import { CommissionBasesSourceType } from '../constants';

const unitOptions = [
  { id: CommissionType.ABSOLUTE, name: '$' },
  { id: CommissionType.PERCENTAGE, name: '%' }
];
interface CommissionRateProps {
  onChange?: (data: { value: string; unit: string; baseType?: CommissionBaseItem }) => void;
  initialData?: {
    value: string;
    unit: string;
    baseType: CommissionBaseItem;
  };
  isExternalCompany?: boolean;
  isListDropdownPortal?: boolean;
}

const CommissionRate: React.FC<CommissionRateProps> = ({
  onChange,
  initialData,
  isExternalCompany = false,
  isListDropdownPortal = false
}) => {
  const commissionBaseMap = useAppSelector(selectCommissionBaseMap);

  const [value, setValue] = useState(initialData?.value || '');
  const [selectedUnit, setSelectedUnit] = useState(
    initialData?.unit ? unitOptions.find(unit => unit.id === initialData.unit) || unitOptions[0] : unitOptions[0]
  );

  const [selectedBaseType, setSelectedBaseType] = useState<CommissionBaseItem>(
    initialData?.baseType ? commissionBaseMap[initialData.baseType.key] : undefined
  );

  // Initialize with initial data when it changes
  useEffect(() => {
    if (initialData) {
      setValue(initialData.value || '');

      const unit = unitOptions.find(unitValue => unitValue.id === initialData.unit) || unitOptions[0];
      setSelectedUnit(unit);

      setSelectedBaseType(initialData.baseType ? commissionBaseMap[initialData.baseType.key] : undefined);
    }
  }, [initialData]);

  const handleValueChange = (newValue: string) => {
    const cleanedValue = cleanNumberInput(newValue);

    if (isValidDecimalInput(cleanedValue)) {
      // Validate percentage value doesn't exceed 100
      if (selectedUnit.id === CommissionType.PERCENTAGE && parseFloat(cleanedValue) > 100) {
        return;
      }

      setValue(cleanedValue);

      if (onChange) {
        onChange({
          value: cleanedValue,
          unit: selectedUnit.id,
          baseType: selectedBaseType && selectedUnit.id === CommissionType.PERCENTAGE ? selectedBaseType : undefined
        });
      }
    }
  };

  const handleUnitChange = (unit: any) => {
    setSelectedUnit(unit);

    if (onChange) {
      onChange({
        value,
        unit: unit.id,
        baseType: unit.id === CommissionType.PERCENTAGE ? selectedBaseType : undefined
      });
    }
  };

  const handleBaseTypeChange = (baseType: CommissionBaseItem) => {
    setSelectedBaseType(baseType);

    if (onChange) {
      onChange({
        value,
        unit: selectedUnit.id,
        baseType: baseType
      });
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center gap-4">
        {/* Value input */}
        <div className="w-[70px]">
          <CustomInput
            id="commission-value"
            value={value}
            type={InputType.NUMBER}
            onChange={handleValueChange}
            placeholder="0.00"
            className="mt-1 h-10 w-[70px] rounded-md border border-borderGray bg-white p-[10px] text-[17px]
               font-normal text-primaryText focus:outline-none"
          />
        </div>
        <div className="w-[100px]">
          <div className="mt-1 flex h-10 overflow-hidden rounded-md border border-borderGray">
            {unitOptions.map(unit => (
              <button
                key={unit.id}
                className={`h-full w-[50px] text-[17px] first-of-type:border-r ${
                  selectedUnit.id === unit.id
                    ? 'bg-[#A4D7FA] font-semibold text-primaryText'
                    : 'font-normal text-accentText'
                }`}
                onClick={() => handleUnitChange(unit)}>
                {unit.name}
              </button>
            ))}
          </div>
        </div>

        {selectedUnit.id === CommissionType.PERCENTAGE && (
          <>
            <div className="mt-1 text-17px font-normal text-primaryText">of</div>
            <div className="min-w-[200px]">
              <ListDropDown
                isPortal={isListDropdownPortal}
                id="commission-base-type"
                items={getCommissionBases({
                  type: isExternalCompany ? CommissionBasesSourceType.REFERRAL : CommissionBasesSourceType.COMMISSION
                })}
                selectedItem={selectedBaseType}
                setSelectedItem={handleBaseTypeChange}
                placeholder="choose base"
                buttonStyles="bg-secondaryBtn h-10 text-[17px] font-semibold text-primaryText pl-[10px] pr-8 py-[9.5px]"
                itemStyle="font-normal text-[17px] tracking-[-0.3px] leading-[28px]"
                iconStyle="right-[9px]"
                spanStyles={`font-normal text-[17px] leading-[21px] ${
                  selectedBaseType ? 'text-primaryText' : 'text-accentText opacity-70'
                }`}
                optionsHeight="h-[185px] z-[999]"
                idKey="key"
                nameKey="label"
              />
            </div>
          </>
        )}
      </div>

      {selectedUnit.id === CommissionType.PERCENTAGE && selectedBaseType && (
        <div className="mt-1 flex items-start text-px13 text-accentText">
          <OutlinedInfoIcon className="mr-2 mt-0.5 shrink-0" width={16} height={16} />
          <div className="whitespace-pre-line">{selectedBaseType.tooltip}</div>
        </div>
      )}
    </div>
  );
};

export default CommissionRate;
